import React from 'react';
import { Box, Typography, Paper, IconButton, Chip } from '@mui/material';
import { Close as CloseIcon, AccessTime as ClockIcon } from '@mui/icons-material';

// Strip trailing emoji from title, return [cleanTitle, emoji]
const EMOJI_RE = /\s*([\p{Emoji_Presentation}\p{Extended_Pictographic}](?:️)?)\s*$/u;
const splitTitleEmoji = (title) => {
  if (!title) return [title, null];
  const match = title.match(EMOJI_RE);
  if (match) return [title.slice(0, match.index).trim(), match[1]];
  return [title, null];
};

// Normalize body text: unify point/coin formats, prevent double-plus
const formatBody = (body) => {
  if (!body) return body;
  return body
    .replace(/\+?(\d+)\s*pistett[äa]/gi, '+$1p')
    .replace(/\+?(\d+)\s*kolikkoa/gi, '+$1 🪙')
    .replace(/—\s*/g, '— ');
};

const WEEKDAYS = ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la'];

const formatTimestamp = (dateStr) => {
  if (!dateStr) return null;
  const normalized = dateStr.replace(' ', 'T');
  const d = new Date(normalized.includes('T') ? normalized : normalized + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Helsinki' });
  const day = WEEKDAYS[d.getDay()];
  return { time, day };
};

const NotificationList = ({ notifications, onDismiss, accent }) => {
  if (notifications.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
        Ei uusia ilmoituksia
      </Typography>
    );
  }

  return notifications.map(n => {
    const [title, emoji] = splitTitleEmoji(n.title);
    const ts = formatTimestamp(n.created_at || n.timestamp);
    return (
      <Paper
        key={n.id}
        variant="outlined"
        sx={{
          p: 1, mb: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1, borderRadius: 1,
          borderColor: accent ? `${accent}33` : 'divider',
          bgcolor: accent ? `${accent}08` : 'transparent',
        }}
      >
        {emoji && (
          <Box sx={{ flexShrink: 0, fontSize: '1.1rem', lineHeight: 1.4, width: 22, textAlign: 'center' }}>
            {emoji}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word', display: 'block' }}>{formatBody(n.body)}</Typography>
          {ts && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              <Chip label={ts.day} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              <Chip icon={<ClockIcon sx={{ fontSize: 14 }} />} label={ts.time} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }} />
            </Box>
          )}
        </Box>
        <IconButton
          onClick={() => onDismiss(n.id)}
          sx={{ width: 28, height: 28, bgcolor: 'action.hover', flexShrink: 0, mt: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Paper>
    );
  });
};

export default React.memo(NotificationList);
